import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Chip, Button, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Pagination, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControl, InputLabel, Select,
  MenuItem, Alert, Divider, Avatar,
} from '@mui/material';
import { Add, Send, ExpandMore } from '@mui/icons-material';
import { ticketsAPI } from '../../api/client';
import useAuthStore from '../../store/useStore';

function TicketsPage() {
  const { user } = useAuthStore();
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [createForm, setCreateForm] = useState({
    subject: '', description: '', category: 'general', priority: 'medium',
  });
  const [error, setError] = useState('');

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await ticketsAPI.list({ page, per_page: 10 });
      setTickets(res.data.tickets || []);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTickets(); }, [page]);

  const handleCreate = async () => {
    setError('');
    try {
      await ticketsAPI.create(createForm);
      setCreateOpen(false);
      setCreateForm({ subject: '', description: '', category: 'general', priority: 'medium' });
      fetchTickets();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ') : 'Failed to create ticket'));
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !detailOpen) return;
    try {
      await ticketsAPI.addMessage(detailOpen.id, { message: newMessage });
      setNewMessage('');
      const res = await ticketsAPI.getById(detailOpen.id);
      setDetailOpen(res.data);
    } catch (err) { console.error(err); }
  };

  const openDetail = async (ticketId) => {
    try {
      const res = await ticketsAPI.getById(ticketId);
      setDetailOpen(res.data);
    } catch (err) { console.error(err); }
  };

  const statusColors = { open: 'warning', in_progress: 'info', resolved: 'success', closed: 'default', escalated: 'error', awaiting_response: 'secondary' };
  const priorityColors = { low: 'default', medium: 'info', high: 'warning', urgent: 'error' };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>Support Tickets</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>New Ticket</Button>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Ticket #</strong></TableCell>
              <TableCell><strong>Subject</strong></TableCell>
              <TableCell><strong>Category</strong></TableCell>
              <TableCell><strong>Priority</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tickets.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>No tickets found</TableCell></TableRow>
            ) : (
              tickets.map((t) => (
                <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => openDetail(t.id)}>
                  <TableCell><Typography fontWeight={600}>{t.ticket_number}</Typography></TableCell>
                  <TableCell>{t.subject}</TableCell>
                  <TableCell><Chip label={t.category.replace('_', ' ')} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} /></TableCell>
                  <TableCell><Chip label={t.priority} size="small" color={priorityColors[t.priority] || 'default'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                  <TableCell><Chip label={t.status.replace('_', ' ')} size="small" color={statusColors[t.status] || 'default'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                  <TableCell>{new Date(t.created_at).toLocaleDateString()}</TableCell>
                  <TableCell><Button size="small">View</Button></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {total > 10 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination count={Math.ceil(total / 10)} page={page} onChange={(e, v) => setPage(v)} color="primary" />
        </Box>
      )}

      {/* Create Ticket Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Support Ticket</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField fullWidth label="Subject" required value={createForm.subject} onChange={(e) => setCreateForm({ ...createForm, subject: e.target.value })} sx={{ mt: 1, mb: 2 }} />
          <TextField fullWidth label="Description" required multiline rows={4} value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} sx={{ mb: 2 }} />
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select value={createForm.category} label="Category" onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}>
                <MenuItem value="general">General</MenuItem>
                <MenuItem value="rent_dispute">Rent Dispute</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
                <MenuItem value="payment_issue">Payment Issue</MenuItem>
                <MenuItem value="landlord_complaint">Landlord Complaint</MenuItem>
                <MenuItem value="tenant_complaint">Tenant Complaint</MenuItem>
                <MenuItem value="property_issue">Property Issue</MenuItem>
                <MenuItem value="eviction">Eviction</MenuItem>
                <MenuItem value="deposit_return">Deposit Return</MenuItem>
                <MenuItem value="contract_issue">Contract Issue</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select value={createForm.priority} label="Priority" onChange={(e) => setCreateForm({ ...createForm, priority: e.target.value })}>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!createForm.subject || !createForm.description}>Create Ticket</Button>
        </DialogActions>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!detailOpen} onClose={() => setDetailOpen(null)} maxWidth="md" fullWidth>
        {detailOpen && (
          <>
            <DialogTitle>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6" fontWeight={700}>Ticket #{detailOpen.ticket_number}</Typography>
                  <Typography variant="body2" color="text.secondary">{detailOpen.subject}</Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Chip label={detailOpen.status.replace('_', ' ')} color={statusColors[detailOpen.status] || 'default'} sx={{ textTransform: 'capitalize' }} />
                  <Chip label={detailOpen.priority} color={priorityColors[detailOpen.priority] || 'default'} sx={{ textTransform: 'capitalize' }} />
                </Stack>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>Description</Typography>
                <Typography>{detailOpen.description}</Typography>
              </Paper>

              <Typography variant="subtitle2" gutterBottom>Messages</Typography>
              <Stack spacing={1.5} sx={{ maxHeight: 300, overflowY: 'auto', mb: 2 }}>
                {(detailOpen.messages || []).map((msg) => (
                  <Paper key={msg.id} variant="outlined" sx={{ p: 2, bgcolor: msg.sender_id === user?.id ? 'primary.50' : 'grey.50' }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>{msg.sender_id === user?.id ? 'You' : 'Support'}</Typography>
                      <Typography variant="caption" color="text.secondary">{new Date(msg.created_at).toLocaleString()}</Typography>
                    </Stack>
                    <Typography variant="body2">{msg.message}</Typography>
                  </Paper>
                ))}
              </Stack>

              {detailOpen.status !== 'closed' && detailOpen.status !== 'resolved' && (
                <Stack direction="row" spacing={1}>
                  <TextField fullWidth size="small" placeholder="Type a message..." value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()} />
                  <Button variant="contained" onClick={handleSendMessage} disabled={!newMessage.trim()}><Send /></Button>
                </Stack>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}

export default TicketsPage;
